import {SearchOutlined} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import dayjs from 'dayjs';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as ArchiveRestore} from '../../Images/ArchiveRestore.svg';
import {ReactComponent as Back} from '../../Images/Back.svg';
import {ReactComponent as FilterIcon} from '../../Images/FilterIcon.svg';
import {
  fetchArchivedDieProfileList,
  unArchiveDieProfile,
} from '../../slices/die-profile';

export default function ArchiveDieProfile() {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const {
    archiveData = [],
    totalRecord,
    loading,
  } = useSelector(state => state.dieProfile);
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('ArchiveDieProfile') || '',
  );
  const [search, setSearch] = useState(
    localStorage.getItem('ArchiveDieProfile') || '',
  );
  const [pagesize, setPageSize] = useState(100);
  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };
  useEffect(() => {
    dispatch(
      fetchArchivedDieProfileList({
        page,
        pagesize,
        ...(search && {search: search}),
        ...(groupSearch && {groupId: groupSearch}),
        ...(orderBy && {ordering: orderBy}),
      }),
    );
  }, [page, pagesize, search, groupSearch, orderBy]);

  const handlePageChange = newPage => {
    setPage(newPage);
    setOrderBy('');
  };

  const handleRestore = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveDieProfile(id))
      .unwrap()
      .then(() => {
        message.success('Profile Unarchived successfully!');
        dispatch(
          fetchArchivedDieProfileList({
            page,
            pagesize,
            ...(search && {search: search}),
            ...(groupSearch && {groupId: groupSearch}),
            ...(orderBy && {ordering: orderBy}),
          }),
        );
      })
      .catch(err => {
        console.error('Delete Error:', err); // Debugging the error
        message.error(
          `Error: ${err.message || 'Failed to delete the profile.'}`,
        );
      });
  };
  const columns = [
    {
      title: 'Section Number',
      dataIndex: 'die_number',
      key: 'die_number',
      width: 100,
      render: die_number => <span>{die_number ? die_number : 'N/A'}</span>,
      sorter: true,
    },
    {
      title: 'Customer Reference No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: 130,
      render: customer_reference_number => (
        <span>
          {customer_reference_number ? customer_reference_number : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Section Image',
      dataIndex: 'die_diagram',
      width: 100,
      key: 'die_diagram',
      render: record => {
        const fileUrl = record;
        if (!fileUrl)
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '70px',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            >
              <span>N/A</span>
            </div>
          );
        const fileExtension = fileUrl.split('.').pop().toLowerCase();
        if (fileExtension === 'pdf') {
          return (
            <Button
              type="link"
              style={{padding: '0px'}}
              onClick={async () => {
                const response = await fetch(fileUrl);

                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const blobData = await response.blob();
                var blob = new Blob([blobData], {type: 'application/pdf'});
                var blobURL = URL.createObjectURL(blob);
                window.open(blobURL);
              }}
            >
              View PDF
            </Button>
          );
        } else if (
          ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)
        ) {
          return (
            <img
              width="70"
              src={fileUrl}
              alt="Die Diagram"
              style={{objectFit: 'contain', maxHeight: '100%'}}
            />
          );
        } else {
          return <span>Unsupported file type</span>;
        }
      },
    },
    {
      title: 'Section Description(mm)',
      dataIndex: 'profileDesc',
      key: 'profileDesc',
      width: 100,
      sorter: false,
      render: (text, record) => {
        const dimensions = [
          record?.dimension1,
          record?.dimension2,
          record?.dimension3,
          record?.dimension4,
        ]
          .filter(dim => dim !== null && dim !== '' && dim !== undefined) // Remove null, empty, and undefined values
          .map(dim => Number(dim).toFixed(2)) // Format valid numbers
          .join(' X ');

        return <span>{dimensions || 'N/A'}</span>;
      },
    },
    {
      title: 'Calculated Kg/Mtr',
      dataIndex: 'wt_kg_p_mt',
      key: 'wt_kg_p_mt',
      width: 100,
      render: wt_kg_p_mt => <span>{wt_kg_p_mt ? wt_kg_p_mt : 'N/A'}</span>,
      sorter: true,
    },
    {
      title: 'Number Of Die Tools',
      dataIndex: 'number_of_dietools',
      key: 'number_of_dietools',
      width: 100,
      render: number_of_dietools => (
        <span>{number_of_dietools ? number_of_dietools : 0}</span>
      ),
      sorter: true,
    },
    {
      title: 'Section Group',
      dataIndex: 'die_group',
      key: 'die_group',
      width: 100,
      sorter: true,
      render: record => {
        return <span>{record?.name ? record?.name : 'N/A'} </span>;
      },
    },
    {
      title: 'Section Type',
      dataIndex: 'die_type',
      key: 'die_type',
      width: 100,
      render: die_type => <span>{die_type ? die_type : 'N/A'}</span>,
      sorter: true,
    },
    {
      title: 'Section Category',
      dataIndex: 'die_category',
      key: 'die_category',
      width: 100,
      sorter: true,
      render: record => {
        return <span>{record?.name ? record?.name : 'N/A'} </span>;
      },
    },
    {
      title: 'Section Sub Category',
      dataIndex: 'die_sub_category',
      key: 'die_sub_category',
      width: 100,
      sorter: true,
      render: record => {
        return <span>{record?.name ? record?.name : 'N/A'} </span>;
      },
    },
    {
      title: 'Total Running KM',
      dataIndex: 'total_running_qty',
      key: 'total_running_qty',
      width: 100,
      render: total_running_qty => (
        <span>{total_running_qty ? total_running_qty : 0}</span>
      ),
      sorter: true,
    },
    {
      title: 'Total MT',
      dataIndex: 'total_running_ton',
      key: 'total_running_ton',
      width: 100,
      render: total_running_ton => (
        <span>{total_running_ton ? total_running_ton : 0}</span>
      ),
      sorter: true,
    },
    {
      title: 'Avg Die Cost',
      dataIndex: 'avgDie',
      key: 'avgDie',
      width: 100,
      sorter: false,
      render: record => {
        return <span>0</span>;
      },
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      width: 100,
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record?.first_name} ${record?.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 100,
      sorter: true,
      render: remarks => {
        return <span>{remarks ? remarks : 'N/A'}</span>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      fixed: 'right',
      key: 'action',
      render: (text, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'die_number' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_number';
    }
    if (sortField === 'die_number' && sortOrder === 'descend') {
      sortingEnumKey = '-die_number';
    }
    if (sortField === 'wt_kg_p_mt' && sortOrder === 'ascend') {
      sortingEnumKey = 'wt_kg_p_mt';
    }
    if (sortField === 'wt_kg_p_mt' && sortOrder === 'descend') {
      sortingEnumKey = '-wt_kg_p_mt';
    }
    if (sortField === 'number_of_dietools' && sortOrder === 'ascend') {
      sortingEnumKey = 'number_of_dietools';
    }
    if (sortField === 'number_of_dietools' && sortOrder === 'descend') {
      sortingEnumKey = '-number_of_dietools';
    }
    if (sortField === 'die_group' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_group';
    }
    if (sortField === 'die_group' && sortOrder === 'descend') {
      sortingEnumKey = '-die_group';
    }
    if (sortField === 'die_category' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_category__name';
    }
    if (sortField === 'die_category' && sortOrder === 'descend') {
      sortingEnumKey = '-die_category__name';
    }
    if (sortField === 'die_sub_category' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_sub_category__name';
    }
    if (sortField === 'die_sub_category' && sortOrder === 'descend') {
      sortingEnumKey = '-die_sub_category__name';
    }
    if (sortField === 'total_running_qty' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_running_qty';
    }
    if (sortField === 'total_running_qty' && sortOrder === 'descend') {
      sortingEnumKey = '-total_running_qty';
    }
    if (sortField === 'total_running_ton' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_running_ton';
    }
    if (sortField === 'total_running_ton' && sortOrder === 'descend') {
      sortingEnumKey = '-total_running_ton';
    }
    if (sortField === 'remarks' && sortOrder === 'ascend') {
      sortingEnumKey = 'remarks';
    }
    if (sortField === 'remarks' && sortOrder === 'descend') {
      sortingEnumKey = '-remarks';
    }
    if (sortField === 'die_type' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_type';
    }
    if (sortField === 'die_type' && sortOrder === 'descend') {
      sortingEnumKey = '-die_type';
    }
    if (sortField === 'updated_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by';
    }
    if (sortField === 'updated_by' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by';
    }
    if (sortField === 'updated_at' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_at';
    }
    if (sortField === 'updated_at' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_at';
    }

    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter && sorter.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('ArchiveDieProfile', value);
  };

  const resetFilter = () => {
    setSearch('');
    setGroupSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable
    localStorage.removeItem('ArchiveDieProfile');
    dispatch(fetchArchivedDieProfileList({page: 1, pagesize}));
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2 style={{margin: 0}}>Archived Section</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-profilelist" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  {title: <Link to="/die-profilelist">Section</Link>},
                  {title: 'Archived Section'},
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                style={{width: 200}}
                onChange={handleSearch}
              />
              <Link>
                <Tooltip placement="top" title={'Reset Filter'}>
                  <Button
                    type="primary"
                    disabled={!search}
                    className="iconlink"
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>
                </Tooltip>
              </Link>
            </Space>
          </Col>
        </Row>
      </div>
      {/* 
        <Table
          rowSelection={rowSelection}
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={status === 'loading'}
          pagination={{
            defaultCurrent: page,
            defaultPageSize: pagesize,
            total: totalRecord,
            showTotal: (total) => `Total ${total} items`,
            showSizeChanger: true,
            onChange: handlePageChange,
            locale: {
              items_per_page: 'Items / Page', // Text for the size changer
            },
          }}
          onChange={handleTableChange}
          style={{ marginTop: '20px' }}
          scroll={{
            x: 800, // Horizontal Scroll
            y: 500, // Vertical Scroll with Fixed Header
          }}
        />
      </div> */}
      <div style={{height: '100vh', display: 'flex', flexDirection: 'column'}}>
        <div style={{flex: 1, overflow: 'auto'}}>
          <Table
            rowKey="id"
            //rowSelection={rowSelection}
            dataSource={archiveData}
            columns={columns}
            pagination={false} // Disable built-in pagination
            loading={loading}
            onChange={handleTableChange}
          />
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{items_per_page: 'Items / Page'}}
          />
        </div>
      </div>
    </>
  );
}
